import React from 'react'
import { PageHeader } from './page-header.js'
import { ReferralHero, type ReferralHeroLabels } from './referral-hero.js'
import { useCountUp } from './use-count-up.js'

export interface ReferralViewLabels {
  /** App page title + lead (PageHeader). */
  pageTitle: string
  pageLead: string
  /** Ink-band hero copy. */
  hero: ReferralHeroLabels
  /** "我的推薦" stats section heading + the three stat-card labels. */
  statsHeading: string
  statReferred: string
  statQualified: string
  statEarned: string
}

export interface ReferralViewData {
  /** Share link, already built from the live origin + code. */
  shareUrl: string
  referredCount: number
  qualifiedCount: number
  totalEarnedTwd: number
}

export interface ReferralViewProps {
  labels: ReferralViewLabels
  data: ReferralViewData
  /** Public how-it-works / FAQ page URL. */
  moreHref: string
  moreTarget?: '_blank'
  onCopy?: (url: string) => void
  /** Optional app-shell context indicator or other title-row action. */
  headerActions?: React.ReactNode
  /** Format the earned amount (NT$). Defaults to `NT$ <grouped>`. */
  formatEarned?: (twd: number) => string
}

function defaultFormatEarned(twd: number): string {
  return `NT$ ${twd.toLocaleString('en-US')}`
}

function StatCard({
  value,
  label,
  format,
  testId,
}: {
  value: number
  label: string
  format?: (n: number) => string
  testId: string
}) {
  const animated = useCountUp(value)
  const text = format ? format(animated) : animated.toLocaleString('en-US')
  return (
    <div className="statcard">
      <div className="n" data-testid={testId}>
        {text}
      </div>
      <div className="l">{label}</div>
    </div>
  )
}

/**
 * The web in-app referral page CONTENT (`/app/referral`), inside the app
 * shell's sidebar. Renders the page header, the reusable {@link
 * ReferralHero} ink band (with the journey animation + copy-link), and the
 * "我的推薦" stat cards (count-up). The full how-it-works steps + FAQ live ONLY
 * on the public page (linked from the hero) to avoid duplicate maintenance.
 */
export function ReferralView({
  labels,
  data,
  moreHref,
  moreTarget,
  onCopy,
  headerActions,
  formatEarned = defaultFormatEarned,
}: ReferralViewProps) {
  return (
    <div data-testid="referral-view">
      <PageHeader
        title={labels.pageTitle}
        subtitle={labels.pageLead}
        actions={headerActions}
      />

      <ReferralHero
        labels={labels.hero}
        shareUrl={data.shareUrl}
        moreHref={moreHref}
        moreTarget={moreTarget}
        onCopy={onCopy}
      />

      <div className="ref-block">
        <h3 className="sec-title">{labels.statsHeading}</h3>
        <div className="statcards">
          <StatCard
            value={data.referredCount}
            label={labels.statReferred}
            testId="referral-stat-referred"
          />
          <StatCard
            value={data.qualifiedCount}
            label={labels.statQualified}
            testId="referral-stat-qualified"
          />
          <StatCard
            value={data.totalEarnedTwd}
            label={labels.statEarned}
            format={formatEarned}
            testId="referral-stat-earned"
          />
        </div>
      </div>
    </div>
  )
}
